import { Box, useTheme } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { tokens } from "../../../theme";
import PopoverModal from "./PopoverModal";

const SHQuickAction = (props) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const [anchorEl, setAnchorEl] = useState(null);
    const touchRef = useRef(null);
    // With `interactive`, the popover content can be clicked. Leaving the trigger then has to hold the
    // popover open briefly, otherwise it closes in the gap between the trigger and the content and the
    // pointer never arrives. Entering the content cancels that pending close.
    const closeTimer = useRef(null);

    const cancelPendingClose = () => {
        if (closeTimer.current) {
            clearTimeout(closeTimer.current);
            closeTimer.current = null;
        }
    };

    useEffect(() => cancelPendingClose, []);

    const handlePopoverOpen = (event) => {
        if(event){
            cancelPendingClose();
            event.stopPropagation();
            event.preventDefault();
            setAnchorEl(event.currentTarget);
        }
    };

    const handlePopoverClose = () => {
        if (props.interactive) {
            cancelPendingClose();
            closeTimer.current = setTimeout(() => setAnchorEl(null), 150);
            return;
        }
        setAnchorEl(null);
    };

    // Separate touch event handler for mobile devices
    // Uses touchstart + native touchend to distinguish taps from scrolls
    const handleTouch = (event) => {
        const touch = event.touches[0];
        const target = event.currentTarget;
        touchRef.current = { x: touch.clientX, y: touch.clientY };

        const onTouchEnd = (e) => {
            target.removeEventListener('touchend', onTouchEnd);
            if (!touchRef.current) return;
            const endTouch = e.changedTouches[0];
            const dx = Math.abs(endTouch.clientX - touchRef.current.x);
            const dy = Math.abs(endTouch.clientY - touchRef.current.y);
            touchRef.current = null;

            // Only toggle if the finger barely moved (tap, not scroll)
            if (dx < 10 && dy < 10) {
                e.stopPropagation();
                e.preventDefault();
                setAnchorEl((prev) => prev ? null : target);
            }
        };
        target.addEventListener('touchend', onTouchEnd);
    };

    return (
        <>
            {props.children({
                anchorEl,
                onMouseEnter:handlePopoverOpen,
                onMouseLeave:handlePopoverClose,
                onTouchStart:handleTouch,
            })}
            {((typeof props.open === "undefined" || props.open === null) || (props.open)) &&
                <PopoverModal
                    id="simple-popover"
                    open={Boolean(anchorEl)}
                    {...props}
                    anchorEl={anchorEl}
                    onClose={handlePopoverClose}
                    triangle={{position:props.position ?? "center",color:theme.palette.mode === "dark" ? colors.grey[300] : "#fff"}}
                    hover={true} // This prop is used to indicate hover functionality
                    sx={props.sx?.modal}
                    disableRestoreFocus={props.disableRestoreFocus ?? false}
                    disableAutoFocus={props.disableAutoFocus ?? false}
                    disableEnforceFocus={props.disableEnforceFocus ?? false}
                    disableScrollLock={props.disableScrollLock ?? false}
                >
                    <Box
                        onMouseEnter={props.interactive ? cancelPendingClose : null}
                        onMouseLeave={props.interactive ? () => setAnchorEl(null) : null}
                        sx={{
                            maxWidth:props.maxWidth ?? 450,
                            background:theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                            px:1.5,
                            py:1.5,
                            "@media screen and (max-width:47.9375em)" : {
                                maxWidth:"unset",
                                // The paper ignores the pointer for hover popovers; on the phone
                                // bottom sheet the content must stay touchable so long lists scroll.
                                pointerEvents:"auto",
                            },
                            ...props.sx,
                        }}
                    >
                        {props.component}
                    </Box>
                </PopoverModal>
            }
        </>
    )
}

export default SHQuickAction;
